import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";
import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";
import { PROTOCOL_VERSION } from "@rivets/core";
import { create } from "zustand";

/**
 * This browser's copy of the workspace shell, so a reload paints from disk and
 * then revalidates: `cached → synchronizing → live`.
 *
 * Two things are kept, both scoped to the signed-in user and both cleared on
 * sign-out, on an access change, and when a different user signs in:
 *  - the shell queries (me, the org, repositories, workers, task lists, task
 *    details, snapshot summaries), through TanStack's `persistQueryClient`;
 *  - the last 20 chats' events with their coverage cursor
 *    (`features/chat/chatPersistence.ts`).
 *
 * File contents, diffs and directory listings are never written: they are read
 * live and are the most sensitive thing on screen. Provisional events are not
 * written either.
 */

export interface KeyValueStore {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  deletePrefix(prefix: string): Promise<void>;
}

export class MemoryStore implements KeyValueStore {
  readonly entries = new Map<string, unknown>();
  async get<T>(key: string) { return this.entries.get(key) as T | undefined; }
  async set(key: string, value: unknown) { this.entries.set(key, structuredClone(value)); }
  async delete(key: string) { this.entries.delete(key); }
  async deletePrefix(prefix: string) {
    for (const key of [...this.entries.keys()]) if (key.startsWith(prefix)) this.entries.delete(key);
  }
}

const DB_NAME = "rivets-cache";
const STORE = "kv";

/** How long the cache may take before the app stops waiting for it. WebKit's
 * IndexedDB can stall outright after the device resumes; a bound turns that
 * into a miss (the network answers instead) rather than a hang. */
export interface IndexedDbTimeouts { open: number; read: number; write: number }
export const INDEXED_DB_TIMEOUTS: IndexedDbTimeouts = { open: 2_000, read: 1_000, write: 5_000 };

export class IndexedDbStore implements KeyValueStore {
  private db: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly factory: IDBFactory = indexedDB,
    private readonly timeouts: IndexedDbTimeouts = INDEXED_DB_TIMEOUTS,
  ) {}

  /** One connection, shared, until it fails or goes away. A rejected or
   * blocked open is not kept, and neither is a connection the browser closed
   * (Safari does after backgrounding) or another tab's upgrade asked to give
   * up: the next call opens afresh instead of failing for the rest of the page. */
  private open(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    const opening = new Promise<IDBDatabase>((resolve, reject) => {
      let settled = false;
      const fail = (error: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      };
      const timer = setTimeout(() => fail(new Error("IndexedDB open timed out")), this.timeouts.open);
      let request: IDBOpenDBRequest;
      try {
        request = this.factory.open(DB_NAME, 1);
      } catch (error) {
        fail(error);
        return;
      }
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onsuccess = () => {
        const db = request.result;
        // Answered after the bound: nobody is waiting, so don't hold it open.
        if (settled) { db.close(); return; }
        settled = true;
        clearTimeout(timer);
        db.onclose = () => this.forget(opening);
        db.onversionchange = () => { db.close(); this.forget(opening); };
        resolve(db);
      };
      request.onerror = () => fail(request.error ?? new Error("IndexedDB open failed"));
      request.onblocked = () => fail(new Error("IndexedDB blocked"));
    });
    this.db = opening;
    opening.catch(() => this.forget(opening));
    return opening;
  }

  private forget(connection: Promise<IDBDatabase>): void {
    if (this.db === connection) this.db = null;
  }

  private async run<T>(mode: IDBTransactionMode, body: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
    const timeoutMs = mode === "readonly" ? this.timeouts.read : this.timeouts.write;
    // A connection that closed under us throws on `transaction`; one reopen
    // covers it.
    for (let attempt = 0; ; attempt++) {
      const connection = this.open();
      const db = await connection;
      let transaction: IDBTransaction;
      try {
        transaction = db.transaction(STORE, mode);
      } catch (error) {
        this.forget(connection);
        if (attempt === 0) continue;
        throw error;
      }
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          // A stalled transaction is a stalled connection: the next call
          // starts on a new one.
          this.forget(connection);
          try { transaction.abort(); } catch { /* already finished */ }
          try { db.close(); } catch { /* already closed */ }
          reject(new Error("IndexedDB transaction timed out"));
        }, timeoutMs);
        const request = body(transaction.objectStore(STORE));
        transaction.oncomplete = () => { clearTimeout(timer); resolve(request ? request.result : undefined); };
        transaction.onerror = () => { clearTimeout(timer); reject(transaction.error); };
        transaction.onabort = () => { clearTimeout(timer); reject(transaction.error ?? new Error("IndexedDB transaction aborted")); };
      });
    }
  }

  get<T>(key: string) { return this.run<T>("readonly", (store) => store.get(key) as IDBRequest<T>); }
  async set(key: string, value: unknown) { await this.run("readwrite", (store) => { store.put(value, key); }); }
  async delete(key: string) { await this.run("readwrite", (store) => { store.delete(key); }); }
  async deletePrefix(prefix: string) {
    await this.run("readwrite", (store) => { store.delete(IDBKeyRange.bound(prefix, `${prefix}\uffff`)); });
  }
}

let store: KeyValueStore | null = typeof indexedDB === "undefined" ? null : new IndexedDbStore();

/** Test seam. `null` turns persistence off. */
export function setPersistentStore(next: KeyValueStore | null): void {
  store = next;
}

export function persistentStore(): KeyValueStore | null {
  return store;
}

// -- user scope -----------------------------------------------------------------

const LAST_USER_KEY = "rivets.cache-user.v1";
/** Bumped whenever a persisted shape changes; anything older is discarded. */
export const PERSIST_BUSTER = `rivets-cache-v1:${PROTOCOL_VERSION}`;
/** A shell older than this is not restored: it would mostly be revalidated
 * away, and a small window keeps what sits on disk small too. */
export const PERSIST_MAX_AGE_MS = 24 * 60 * 60_000;

export function userPrefix(userId: string): string {
  return `u:${userId}:`;
}

function localStorageSafe(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** The user whose cache this browser holds, readable before the session
 * check answers so the shell can paint from disk. */
export function cachedUserId(): string | null {
  try {
    return localStorageSafe()?.getItem(LAST_USER_KEY) ?? null;
  } catch {
    return null;
  }
}

function setCachedUserId(userId: string | null): void {
  try {
    const storage = localStorageSafe();
    if (!storage) return;
    if (userId) storage.setItem(LAST_USER_KEY, userId);
    else storage.removeItem(LAST_USER_KEY);
  } catch {
    /* storage unavailable: the cache simply is not restored next time */
  }
}

/**
 * Where the shell's data came from, for the `cached → synchronizing → live`
 * status.
 *  - `restoring`: the disk read is in flight (bounded); the app waits for it,
 *    and for the session, before rendering anything.
 *  - `restoredUserId`: whose shell is in memory from disk. Nothing renders
 *    until the session names this same user; any other answer clears it.
 *  - `restored`: what is on screen was read from this browser.
 */
export const useCacheState = create<{ restored: boolean; restoring: boolean; restoredUserId: string | null; userId: string | null }>(
  () => ({ restored: false, restoring: false, restoredUserId: null, userId: null }),
);

// -- query persistence -----------------------------------------------------------

/** The shell: small, per-user, and what the first paint needs. */
const PERSISTED_QUERY_ROOTS = new Set(["me", "org", "repos", "workers", "worktrees", "worktree", "snapshot", "home-stats"]);

export function shouldPersistQuery(queryKey: readonly unknown[], status: string, data?: unknown): boolean {
  if (status !== "success" || typeof queryKey[0] !== "string" || !PERSISTED_QUERY_ROOTS.has(queryKey[0])) return false;
  // A LocalApi summary (`lsnap_…`) means nothing after a reload: every diff
  // opened from it would 410 back to the relay's summary.
  if (queryKey[0] === "snapshot" && typeof (data as { id?: unknown } | null)?.id === "string" && (data as { id: string }).id.startsWith("lsnap_")) return false;
  return true;
}

/** A shell write waits for the cache to be quiet this long. Each write
 * structured-clones the whole shell into IndexedDB on the main thread (about
 * 0.4 ms for a typical shell, 5 ms for a large one), and while agents stream,
 * `snapshot.updated` keeps patching it. */
export const PERSIST_IDLE_MS = 2_000;
/** And never longer than this, so a shell that never goes quiet still lands. */
export const PERSIST_MAX_WAIT_MS = 10_000;

/** Writes the shell for `userId`, as long as that user is still the active one. */
export function createQueryPersister(kv: KeyValueStore, userId: string): Persister {
  const key = `${userPrefix(userId)}queries`;
  return {
    persistClient: async (client) => {
      if (activeUserId === userId) await kv.set(key, client).catch(() => undefined);
    },
    restoreClient: () => kv.get<PersistedClient>(key),
    removeClient: () => kv.delete(key),
  };
}

/**
 * TanStack's `persistQueryClientSubscribe`, made cheap to leave running: it
 * dehydrated on every cache event, file and directory queries included. This
 * only listens to the shell's own queries, waits for the cache to go quiet (or
 * for the max wait), then dehydrates once when the main thread is idle. Hiding
 * or leaving the page writes at once: a backgrounded tab may get no other
 * chance.
 */
export function subscribeQueryPersistence(
  queryClient: QueryClient,
  persister: Persister,
  { idleMs = PERSIST_IDLE_MS, maxWaitMs = PERSIST_MAX_WAIT_MS }: { idleMs?: number; maxWaitMs?: number } = {},
): () => void {
  let dirty = false;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  let maxTimer: ReturnType<typeof setTimeout> | undefined;
  let idleCallback: number | undefined;
  const clearScheduled = () => {
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    if (maxTimer !== undefined) clearTimeout(maxTimer);
    if (idleCallback !== undefined && typeof cancelIdleCallback === "function") cancelIdleCallback(idleCallback);
    idleTimer = maxTimer = idleCallback = undefined;
  };
  const save = () => {
    clearScheduled();
    if (!dirty) return;
    dirty = false;
    void persister.persistClient({
      buster: PERSIST_BUSTER,
      timestamp: Date.now(),
      clientState: dehydrate(queryClient, { shouldDehydrateQuery: (query) => shouldPersistQuery(query.queryKey, query.state.status, query.state.data) }),
    });
  };
  const saveWhenIdle = () => {
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    if (maxTimer !== undefined) clearTimeout(maxTimer);
    idleTimer = maxTimer = undefined;
    if (typeof requestIdleCallback !== "function") { save(); return; }
    idleCallback ??= requestIdleCallback(() => { idleCallback = undefined; save(); }, { timeout: 1_000 });
  };
  const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "added" && event.type !== "updated" && event.type !== "removed") return;
    const root = event.query.queryKey[0];
    if (typeof root !== "string" || !PERSISTED_QUERY_ROOTS.has(root)) return;
    dirty = true;
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = setTimeout(saveWhenIdle, idleMs);
    maxTimer ??= setTimeout(saveWhenIdle, maxWaitMs);
  });
  const onVisibility = () => { if (document.visibilityState === "hidden") save(); };
  const hasWindow = typeof window !== "undefined" && typeof window.addEventListener === "function";
  const hasDocument = typeof document !== "undefined" && typeof document.addEventListener === "function";
  if (hasWindow) window.addEventListener("pagehide", save);
  if (hasDocument) document.addEventListener("visibilitychange", onVisibility);
  return () => {
    unsubscribeCache();
    clearScheduled();
    dirty = false;
    if (hasWindow) window.removeEventListener("pagehide", save);
    if (hasDocument) document.removeEventListener("visibilitychange", onVisibility);
  };
}

let activeUserId: string | null = null;
let unsubscribeQueries: (() => void) | null = null;
const userListeners = new Set<(userId: string | null) => void>();

/** Other persisted layers (chat events) follow the active user. */
export function onPersistenceUser(listener: (userId: string | null) => void): () => void {
  userListeners.add(listener);
  return () => userListeners.delete(listener);
}

export function persistenceUserId(): string | null {
  return activeUserId;
}

/** Bumped by every restore, activation and clear: a restore that resolves
 * after any of those (or after its timeout) is dropped, never hydrated. */
let restoreGeneration = 0;

/**
 * Reads the last user's shell into memory in parallel with the session check.
 * Nothing is rendered from it here: `AuthedLayout` waits for the session, and
 * shows it only when the session names the same user (otherwise it is
 * cleared). Bounded, because a slow disk must not hold the page back longer
 * than the network would have; a read that answers after the bound, or after
 * a clear or another user's activation, is thrown away.
 */
export async function restorePersistedShell(queryClient: QueryClient, timeoutMs = 250): Promise<boolean> {
  const kv = store;
  const userId = cachedUserId();
  if (!kv || !userId) return false;
  const generation = ++restoreGeneration;
  useCacheState.setState({ restoring: true });
  let timedOut = false;
  const timeout = new Promise<null>((resolve) => setTimeout(() => { timedOut = true; resolve(null); }, timeoutMs));
  const persisted = await Promise.race([
    kv.get<PersistedClient>(`${userPrefix(userId)}queries`).then((value) => value ?? null, () => null),
    timeout,
  ]);
  if (generation === restoreGeneration) useCacheState.setState({ restoring: false });
  const current = !timedOut && generation === restoreGeneration;
  if (!current || !persisted) return false;
  if (persisted.buster !== PERSIST_BUSTER || Date.now() - persisted.timestamp > PERSIST_MAX_AGE_MS) {
    void kv.delete(`${userPrefix(userId)}queries`).catch(() => undefined);
    return false;
  }
  hydrate(queryClient, persisted.clientState);
  const hasShell = queryClient.getQueryCache().getAll().length > 0;
  useCacheState.setState({ restored: hasShell, restoredUserId: hasShell ? userId : null });
  return hasShell;
}

/**
 * The session answered with `userId`. A shell restored for anyone else is
 * cleared before anything renders from it, and that user's disk copy goes
 * too; then writes follow this user.
 */
export function activatePersistence(queryClient: QueryClient, userId: string): void {
  const kv = store;
  restoreGeneration += 1;
  const { restoredUserId } = useCacheState.getState();
  if ((restoredUserId && restoredUserId !== userId) || (activeUserId && activeUserId !== userId)) {
    queryClient.clear();
    useCacheState.setState({ restored: false });
  }
  if (activeUserId && activeUserId !== userId) void kv?.deletePrefix(userPrefix(activeUserId)).catch(() => undefined);
  const previous = cachedUserId();
  if (previous && previous !== userId) void kv?.deletePrefix(userPrefix(previous)).catch(() => undefined);
  setCachedUserId(userId);
  const changed = activeUserId !== userId;
  activeUserId = userId;
  useCacheState.setState({ userId, restoredUserId: null, restoring: false });
  if (changed) {
    // Writes follow the new user from here; the old subscription wrote theirs.
    unsubscribeQueries?.();
    unsubscribeQueries = null;
    for (const listener of userListeners) listener(userId);
  }
  if (unsubscribeQueries || !kv) return;
  unsubscribeQueries = subscribeQueryPersistence(queryClient, createQueryPersister(kv, userId));
}

/** Sign-out, a signed-out session, or an access change: nothing of this user
 * stays on disk. */
export async function clearPersistedData(): Promise<void> {
  const userId = activeUserId ?? cachedUserId();
  restoreGeneration += 1;
  unsubscribeQueries?.();
  unsubscribeQueries = null;
  activeUserId = null;
  setCachedUserId(null);
  useCacheState.setState({ restored: false, restoring: false, restoredUserId: null, userId: null });
  for (const listener of userListeners) listener(null);
  if (userId && store) await store.deletePrefix(userPrefix(userId)).catch(() => undefined);
}
